/**
 * The one way a report failure looks — CLAUDE.md §4.10, §5.8. It reads the RFC 9457 problem detail
 * and says what happened and what to do, never vaguely and never apologetically. **Nothing internal
 * is shown**: no `error.message` for anything that is not a problem detail, only the server's
 * `reference` when there is one, which is what lets support find the log entry.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';

interface Explanation {
  title: string;
  guidance: string;
  reference: string | undefined;
}

function explain(error: Error, text: (english: string) => string): Explanation {
  if (!(error instanceof ApiError)) {
    // A thrown non-ApiError means the request never reached the API — the browser is offline, or
    // something between here and the ingress dropped it.
    return {
      title: text('WinSight could not be reached'),
      guidance: text('Check your connection, then reload the page.'),
      reference: undefined,
    };
  }

  const { status, detail, reference } = error.problem;
  const title = error.problem.title;

  if (status === 401) {
    return {
      title: text('Your session has ended'),
      guidance: 'Sign in again to carry on. Your filters are in the address bar and will survive it.',
      reference,
    };
  }
  if (status === 403) {
    return {
      title: text('This report is not shared with your role'),
      guidance: 'Ask your manager to request access through Base Portal.',
      reference,
    };
  }
  if (status === 404) {
    return {
      title: text('This report no longer exists'),
      guidance: 'It may have been renamed or replaced. Look for it in the menu under its operation.',
      reference,
    };
  }
  if (status === 400 || status === 422) {
    return {
      title,
      guidance: detail ?? 'Check the filters above, then run the report again.',
      reference,
    };
  }
  if (status === 503) {
    return {
      title: text('WinSight is temporarily unavailable'),
      guidance: 'A service it depends on did not answer. Wait a moment and reload the page.',
      reference,
    };
  }

  return {
    title: status >= 500 ? text('The report could not be produced') : title,
    guidance:
      status >= 500
        ? 'Nothing is wrong with your filters. Reload to try again; if it keeps happening, send IT the reference below.'
        : (detail ?? 'Reload the page to try again.'),
    reference,
  };
}

export function ReportError({ error }: { error: Error }) {
  const { text } = useI18n();
  const { title, guidance, reference } = explain(error, text);

  return (
    <Alert variant="destructive">
      <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <p>{guidance}</p>
        {reference !== undefined ? (
          <p className="text-xs">
            {text('Reference')} <span className="font-mono text-foreground">{reference}</span>
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
